import {
  Box,
  Button,
  Icon,
  Theme,
  Typography,
  useTheme,
} from "@outschool/backpack";
import { faChevronLeft, faChevronRight, faCircleCheck } from "@outschool/icons";
import { TFunction, useTranslation } from "@outschool/localization";
import { useIsMobile } from "@outschool/ui-utils";
import React from "react";

import TutoringCLPAgeFilter from "./TutoringCLPAgeFilter";
import TutoringCLPdowFilter from "./TutoringCLPdowFilter";
import TutoringCLPFrequencyFilter from "./TutoringCLPFrequencyFilter";
import TutoringCLPTimeFilter from "./TutoringCLPTimeFilter";

export type CategoryTutoringFiltersProps = {
  categorySlug?: string;
  updateListings: (val: any) => void;
  label: string;
  t: TFunction;
  searchFilters?: any;
};

export function HeroFilters({
  categorySlug,
  updateSearchFilters,
  searchFilters,
  setLastStep,
}: {
  categorySlug?: string;
  updateSearchFilters: (val: any) => void;
  searchFilters: any;
  setLastStep?: (setLastStep: boolean) => void;
}) {
  const { t } = useTranslation(
    "client\\components\\categories\\CategoryTutoringFilters",
  );
  const [currentStep, setCurrentStep] = React.useState(
    !!searchFilters.age ? 1 : 0,
  );
  const [nextDisabled, setNextDisabled] = React.useState(!!!searchFilters.age);
  const theme = useTheme();
  const isMobile = useIsMobile();

  const successTextTop = isMobile
    ? t("We’ve updated your results just for you!")
    : t("We’ve updated your results below just for you!");
  const successTextBottom = isMobile
    ? t("Use the filters to continue adjusting your results!")
    : t("Use the filters below to continue adjusting your results!");

  React.useEffect(() => {
    switch (currentStep) {
      case 0: {
        setNextDisabled(!!!searchFilters.age);
        break;
      }
      case 1: {
        setNextDisabled(!!!searchFilters.dow);
        break;
      }
      case 2: {
        setNextDisabled(
          !!!searchFilters.startAfterTime && !!!searchFilters.endByTime,
        );
        break;
      }
      case 3: {
        setNextDisabled(
          !!!searchFilters.weeklyMeetingsMin &&
            !!!searchFilters.weeklyMeetingsMax,
        );
        break;
      }
      case 4: {
        setLastStep?.(true);
      }
    }
  }, [searchFilters, currentStep, setNextDisabled, setLastStep]);

  return (
    <Box
      sx={{
        padding: "16px",
        backgroundColor: "white",
        width: "550px",
        borderRadius: "16px",
        boxShadow: "0px 16px 32px 0px rgba(0, 0, 0, 0.1)",
      }}
    >
      {currentStep === 4 ? (
        <Box
          flex
          sx={(theme: Theme) => ({
            flexDirection: "column",
            gap: "1em",
            alignItems: "center",
            height: "100%",
            [theme.breakpoints.down("md")]: {
              justifyContent: "center",
              textAlign: "center",
            },
          })}
        >
          <Icon
            icon={faCircleCheck}
            sx={{ color: theme.palette.success[500] }}
            size={"large"}
          />
          <Typography
            variant={"h4"}
            emphasized
            sx={{
              padding: "0.5em",
              [theme.breakpoints.up("md")]: {
                borderBottom: "1px solid",
                borderColor: theme.palette.grey[300],
              },
            }}
          >
            {successTextTop}
          </Typography>
          <Typography
            variant={"h6"}
            sx={(theme: Theme) => ({
              color: theme.palette.grey[600],
            })}
          >
            {successTextBottom}
          </Typography>
        </Box>
      ) : (
        <Box
          flex
          sx={{
            flexDirection: "column",
            justifyContent: "space-between",
            height: "100%",
          }}
        >
          {currentStep === 0 ? (
            <TutoringCLPAgeFilter
              categorySlug={categorySlug}
              updateListings={age => {
                updateSearchFilters({
                  ...searchFilters,
                  age: searchFilters.age + "," + age,
                });
              }}
              label={t("How old is your learner?")}
              t={t}
              searchFilters={searchFilters}
            />
          ) : currentStep === 1 ? (
            <TutoringCLPdowFilter
              categorySlug={categorySlug}
              updateListings={day => {
                updateSearchFilters({
                  ...searchFilters,
                  dow: searchFilters.dow + "," + day,
                });
              }}
              label={t("Which days are you available?")}
              t={t}
              searchFilters={searchFilters}
            />
          ) : currentStep === 2 ? (
            <TutoringCLPTimeFilter
              categorySlug={categorySlug}
              updateListings={val => {
                updateSearchFilters({
                  ...searchFilters,
                  startAfterTime: val.min,
                  endByTime: val.max,
                });
              }}
              label={t("What time are you available?")}
              t={t}
              searchFilters={searchFilters}
            />
          ) : currentStep === 3 ? (
            <TutoringCLPFrequencyFilter
              categorySlug={categorySlug}
              updateListings={val => {
                updateSearchFilters({
                  ...searchFilters,
                  weeklyMeetingsMin: val.min,
                  weeklyMeetingsMax: val.max,
                });
              }}
              label={t("How often do you want to meet?")}
              t={t}
              searchFilters={searchFilters}
            />
          ) : (
            <></>
          )}

          <Box
            flex
            sx={{
              justifyContent: currentStep === 0 ? "end" : "space-between",
              paddingTop: "12px",
            }}
          >
            {currentStep !== 0 && (
              <Button
                variant={"text"}
                onClick={() => {
                  setCurrentStep(currentStep - 1);
                }}
                sx={{ padding: "0", backgroundColor: "white !important" }}
              >
                <Icon
                  icon={faChevronLeft}
                  sx={{
                    width: "24px",
                    height: "24px",
                  }}
                />
                {t("Back")}
              </Button>
            )}
            <Button
              variant={"contained"}
              color={"primary"}
              onClick={() => {
                setCurrentStep(currentStep + 1);
              }}
              disabled={nextDisabled}
              sx={{ gap: "5px" }}
            >
              {t("Next")}
              <Icon icon={faChevronRight} />
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
